<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center" @keydown.esc="tryClose">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/20" @click="tryClose" />

    <!-- Modal -->
    <div class="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">
          New {{ modalTitle }}
        </h3>
        <button class="text-gray-400 hover:text-gray-600" @click="tryClose">
          <FontAwesomeIcon :icon="['fas', 'times']" />
        </button>
      </div>

      <div class="px-6 py-4 space-y-4">
        <!-- Title -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Title</label>
          <input
            ref="titleInput"
            v-model="form.title"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            :placeholder="isTicketType ? 'Describe the issue' : 'Enter title'"
          />
        </div>

        <!-- Type -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <select
            v-model="form.requirement_type"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option
              v-for="t in filteredTypes"
              :key="t.id"
              :value="t.id"
            >
              {{ t.name }}
            </option>
          </select>
        </div>

        <!-- Tenant -->
        <div v-if="!customerFacing">
          <label class="block text-xs font-medium text-gray-500 mb-1">Tenant</label>
          <select
            v-model="form.tenant"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            @change="onTenantChange"
          >
            <option
              v-for="t in tenantChoices"
              :key="t.id"
              :value="t.id"
            >
              {{ t.name }}
            </option>
          </select>
        </div>

        <!-- Requester (staff only). The person the ticket is for; they get the
             confirmation and every status email. Blank means nobody is emailed. -->
        <div v-if="!customerFacing">
          <label class="block text-xs font-medium text-gray-500 mb-1">Requester</label>
          <div class="relative">
            <input
              v-model="requesterSearch"
              type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              :placeholder="requesterName || (form.tenant ? 'Search users in this tenant...' : 'Pick a tenant first')"
              :disabled="!form.tenant"
              @click.stop="showRequesterDropdown = true"
              @focus="showRequesterDropdown = true"
              @input="debouncedSearchRequesters"
            />
            <button
              v-if="form.requester"
              type="button"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              title="Clear requester"
              @click.stop="clearRequester"
            >
              <FontAwesomeIcon :icon="['fas', 'xmark']" />
            </button>
            <div
              v-if="showRequesterDropdown && requesterResults.length"
              class="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
              @click.stop
            >
              <button
                v-for="u in requesterResults"
                :key="u.id"
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                :class="u.id === form.requester ? 'bg-primary-50 text-primary-700' : 'text-gray-700'"
                @click="selectRequester(u)"
              >
                <span class="sensitive">{{ u.full_name || u.email }}</span> <span class="text-[11px] text-gray-400 ml-1 sensitive">{{ u.email }}</span>
              </button>
            </div>
          </div>
          <p class="text-[11px] text-gray-400 mt-1">
            {{ form.requester ? 'They will receive the confirmation and status emails.' : 'Nobody will be emailed about this ticket.' }}
          </p>
        </div>

        <!-- Customer visibility (staff only). Internal by default. -->
        <div v-if="!customerFacing" class="flex items-start gap-2">
          <input
            id="create-customer-facing"
            v-model="form.customer_facing"
            type="checkbox"
            class="h-4 w-4 mt-0.5 rounded border-gray-300 accent-primary"
          />
          <label for="create-customer-facing" class="text-xs text-gray-600">
            <span class="font-medium text-gray-700">Visible to the customer</span>
            <span class="block text-[11px] text-gray-400">Shows on their Service Desk and sends them the confirmation email. Leave off for internal work.</span>
          </label>
        </div>

        <!-- Parent (searchable). Every work type has legal parents now, including
             Bug and Support Ticket, so this is gated on staff mode alone. -->
        <div v-if="!customerFacing">
          <label class="block text-xs font-medium text-gray-500 mb-1">Parent (optional)</label>
          <Support_SearchableSelect
            v-model="form.parent"
            :options="validParentOptions"
            placeholder="None (top-level)"
          />
        </div>

        <!-- Priority (internal triage signal -- staff only; customer
             submissions come in unprioritized for staff to triage) -->
        <div v-if="!customerFacing">
          <label class="block text-xs font-medium text-gray-500 mb-1">Priority</label>
          <Support_PriorityRating
            :model-value="form.priority"
            @update:model-value="form.priority = $event"
          />
        </div>

        <!-- Owner -->
        <div v-if="!customerFacing">
          <label class="block text-xs font-medium text-gray-500 mb-1">Owner</label>
          <select
            v-model="form.owner"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option :value="null">Unassigned</option>
            <option
              v-for="u in users"
              :key="u.id"
              :value="u.id"
              class="sensitive"
            >
              {{ u.full_name }}
            </option>
          </select>
        </div>

        <!-- Tags (staff only) -->
        <div v-if="!customerFacing">
          <label class="block text-xs font-medium text-gray-500 mb-1">Tags</label>
          <Support_TagEditor v-model="form.tags" />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Description</label>
          <textarea
            v-model="form.description"
            rows="2"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none overflow-y-auto max-h-48"
            :placeholder="isTicketType ? 'Steps to reproduce, expected behavior, etc.' : 'Describe the requirement'"
            @input="autoResize"
          />
        </div>

        <!-- Attachments -->
        <RequirementAttachmentUpload
          ref="attachUpload"
          :requirement-id="null"
          :surface="true"
          :drop-label="`to this ${modalTitle.toLowerCase()}`"
          deferred-verb="click Create"
          @files-changed="onAttachFilesChanged"
        />
      </div>

      <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
        <button
          class="text-sm text-gray-600 hover:text-gray-800 px-4 py-2"
          @click="tryClose"
        >
          Cancel
        </button>
        <button
          class="bg-primary-500 text-white text-sm font-medium px-4 py-2 rounded hover:bg-primary-600 transition-colors"
          :disabled="!form.title || !form.requirement_type || uploading"
          @click="create"
        >
          {{ uploading ? 'Submitting...' : 'Create' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useToast } from 'vue-toastification';
import Support_PriorityRating from './Support_PriorityRating.vue';
import Support_SearchableSelect from './Support_SearchableSelect.vue';
import Support_TagEditor from './Support_TagEditor.vue';
import RequirementAttachmentUpload from './RequirementAttachmentUpload.vue';
import debounce from 'lodash/debounce';
import { VALID_PARENT_TYPES, WORK_TYPES, priorityInt } from './support_constants.js';

const TICKET_TYPE_NAMES = new Set(['Bug', 'Support Ticket']);
const CUSTOMER_FACING_TYPE_NAMES = new Set(['Bug', 'Support Ticket', 'Feature']);

export default {
  name: 'Support_CreateModal',
  components: { FontAwesomeIcon, Support_PriorityRating, Support_SearchableSelect, Support_TagEditor, RequirementAttachmentUpload },
  props: {
    requirementTypes: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    parentOptions: { type: Array, default: () => [] },
    tenantOptions: { type: Array, default: () => [] },
    customerFacing: { type: Boolean, default: false },
    defaultTypeName: { type: String, default: null },
    defaultParent: { type: [Number, String], default: null },
  },
  emits: ['close', 'created'],
  data() {
    const filteredTypes = this.getFilteredTypes();
    const matchedType = this.defaultTypeName
      ? filteredTypes.find(t => t.name === this.defaultTypeName)
      : null;
    const defaultType = matchedType?.id ?? (filteredTypes.length ? filteredTypes[0].id : null);
    const defaultTypeName = filteredTypes.find(t => t.id === defaultType)?.name;
    const isTicketMode = this.customerFacing || TICKET_TYPE_NAMES.has(defaultTypeName);
    const parent = this.defaultParent
      ? this.parentOptions.find((r) => r.id === Number(this.defaultParent))
      : null;
    return {
      uploading: false,
      hasAttachments: false,
      tenantsWithSlugs: [],
      requesterSearch: '',
      requesterResults: [],
      requesterName: '',
      showRequesterDropdown: false,
      form: {
        title: '',
        requirement_type: defaultType,
        tenant: parent?.tenant ?? null,
        parent: this.defaultParent || null,
        owner: null,
        requester: null,
        due_date: null,
        tags: [],
        description: '',
        // Customer submissions arrive unprioritized; staff triages. Staff
        // ticket-mode keeps the P2 default.
        priority: isTicketMode && !this.customerFacing ? 2 : null,
        status: isTicketMode ? 'open' : 'draft',
        customer_facing: !!this.customerFacing,
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug', 'current_tenant_name']),
    toast() {
      return useToast();
    },
    tenantChoices() {
      const seen = new Map();
      for (const t of this.tenantOptions) seen.set(t.id, { id: t.id, name: t.name });
      for (const t of this.tenantsWithSlugs) if (!seen.has(t.id)) seen.set(t.id, { id: t.id, name: t.name });
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    selectedTenantSlug() {
      return this.tenantsWithSlugs.find((t) => t.id === this.form.tenant)?.slug || null;
    },
    filteredTypes() {
      return this.getFilteredTypes();
    },
    selectedTypeName() {
      return this.requirementTypes.find(t => t.id === this.form.requirement_type)?.name || null;
    },
    isTicketType() {
      return TICKET_TYPE_NAMES.has(this.selectedTypeName);
    },
    modalTitle() {
      return this.selectedTypeName || (this.customerFacing ? 'Request' : 'Requirement');
    },
    isCustomerFacingType() {
      const type = this.requirementTypes.find(t => t.id === this.form.requirement_type);
      return type ? CUSTOMER_FACING_TYPE_NAMES.has(type.name) : false;
    },
    isDirty() {
      return (
        this.form.title.trim() !== '' ||
        this.form.description.trim() !== '' ||
        this.hasAttachments
      );
    },
    validParentOptions() {
      const selectedType = this.requirementTypes.find((t) => t.id === this.form.requirement_type);
      const typeName = selectedType?.name;
      const validParents = VALID_PARENT_TYPES[typeName];
      if (!validParents) return this.parentOptions;
      return this.parentOptions.filter((item) => validParents.includes(item.requirement_type_name));
    },
    hierarchicalParents() {
      // Build indented list from flat data
      const byId = {};
      for (const item of this.parentOptions) {
        byId[item.id] = item;
      }
      const result = [];
      const roots = this.parentOptions.filter(r => !r.parent);

      const walk = (items, depth) => {
        for (const item of items) {
          result.push({
            id: item.id,
            title: item.title,
            indent: '\u00A0\u00A0'.repeat(depth),
          });
          const children = this.parentOptions.filter(r => r.parent === item.id);
          if (children.length) walk(children, depth + 1);
        }
      };
      walk(roots, 0);
      return result;
    },
  },
  watch: {
    'form.requirement_type'() {
      if (this.form.parent) {
        const stillValid = this.validParentOptions.some((o) => o.id === this.form.parent);
        if (!stillValid) this.form.parent = null;
      }
      // Status is never shown in this modal, so it has to follow the type
      // rather than whatever the modal opened as: a Bug goes straight to 'open'
      // for triage, a Requirement starts life as a draft.
      if (!this.customerFacing) {
        this.form.status = this.isTicketType ? 'open' : 'draft';
      }
    },
    'form.parent'(parentId) {
      if (!parentId) return;
      const parent = this.parentOptions.find(r => r.id === parentId);
      if (!parent) return;
      const inherited = priorityInt(parent.priority);
      if (inherited != null) this.form.priority = inherited;
      if (parent.tenant && parent.tenant !== this.form.tenant) {
        this.form.tenant = parent.tenant;
        this.onTenantChange();
      }
    },
  },
  created() {
    this.debouncedSearchRequesters = debounce(this.searchRequesters, 300);
  },
  mounted() {
    this.$nextTick(() => this.$refs.titleInput?.focus());
    if (!this.customerFacing) this.loadTenants();
    document.addEventListener('click', this.closeRequesterDropdown);
    this._beforeUnload = (e) => {
      if (this.isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', this._beforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this._beforeUnload);
    document.removeEventListener('click', this.closeRequesterDropdown);
  },
  methods: {
    closeRequesterDropdown() {
      this.showRequesterDropdown = false;
    },
    // Slugs are needed to search a tenant's users; the row-derived options only
    // carry ids and names. The current tenant becomes the default.
    async loadTenants() {
      try {
        const { data } = await axios.get('/api/tenants/');
        this.tenantsWithSlugs = Array.isArray(data) ? data : data.results || [];
      } catch {
        this.tenantsWithSlugs = [];
      }
      if (this.form.tenant == null) {
        const current = this.tenantsWithSlugs.find((t) => t.slug === this.current_tenant_slug)
          || this.tenantChoices.find((t) => t.name === this.current_tenant_name);
        this.form.tenant = current?.id ?? this.tenantChoices[0]?.id ?? null;
      }
    },
    onTenantChange() {
      this.clearRequester();
      this.requesterResults = [];
    },
    async searchRequesters() {
      if (!this.selectedTenantSlug) return;
      try {
        const { data } = await axios.get('/api/admin/user/', {
          params: { search: this.requesterSearch || undefined, tenant_slug: this.selectedTenantSlug },
        });
        this.requesterResults = Array.isArray(data) ? data : data.results || [];
      } catch {
        this.requesterResults = [];
      }
    },
    selectRequester(user) {
      this.form.requester = user.id;
      this.requesterName = user.full_name || user.email;
      this.requesterSearch = '';
      this.showRequesterDropdown = false;
    },
    clearRequester() {
      this.form.requester = null;
      this.requesterName = '';
      this.requesterSearch = '';
    },
    tryClose() {
      if (this.isDirty) {
        if (!window.confirm('You have unsaved changes. Discard this submission?')) {
          return;
        }
      }
      this.$emit('close');
    },
    getFilteredTypes() {
      // Staff pick from every work type -- the merged list has no tab to
      // narrow by. Customers still only see the three they can file.
      const allowed = this.customerFacing ? CUSTOMER_FACING_TYPE_NAMES : WORK_TYPES;
      return this.requirementTypes.filter(t => allowed.has(t.name));
    },
    autoResize(e) {
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    onAttachFilesChanged(files) {
      this.hasAttachments = files.length > 0;
    },
    async create() {
      this.uploading = true;
      try {
        const priorityToApi = { 0: 'p0', 1: 'p1', 2: 'p2', 3: 'p3' };
        const payload = {
          ...this.form,
          priority: priorityToApi[this.form.priority] ?? 'none',
          customer_facing: this.customerFacing || !!this.form.customer_facing,
        };
        if (this.customerFacing) {
          // The backend sets the requester to the caller for customers.
          delete payload.requester;
          delete payload.tenant;
        }

        const response = await axios.post('/api/requirements/', payload);
        const requirementId = response.data.id;

        if (this.$refs.attachUpload?.hasFiles()) {
          // uploadAllStaged never throws — it marks each failed row and keeps
          // going so one bad file can't strand the rest. Compare counts to
          // find out whether anything was actually lost, otherwise a ticket
          // with zero attached files still reports "submitted successfully".
          const staged = this.$refs.attachUpload.getStagedFiles().length;
          const uploaded = await this.$refs.attachUpload.uploadAllStaged(
            requirementId
          );
          const failed = staged - uploaded.length;
          if (failed > 0) {
            this.toast.warning(
              `${this.modalTitle} created, but ${failed} attachment${failed === 1 ? '' : 's'} failed to upload. You can add ${failed === 1 ? 'it' : 'them'} from the ticket.`,
            );
            this.$emit('created', response.data);
            return;
          }
        }

        this.toast.success(`${this.modalTitle} submitted successfully.`);
        this.$emit('created', response.data);
      } catch (err) {
        const detail =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          (err.response?.status === 403
            ? 'You do not have permission to submit requests.'
            : null);
        this.toast.error(detail || 'Something went wrong. Please try again.');
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>
